import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';

type ToastVariant = 'success' | 'error' | 'info';

const MAX_TOASTS = 3;

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: () => {},
});

const variantStyles: Record<ToastVariant, { bg: string; color: string; borderColor: string }> = {
  success: { bg: 'status.success.bg', color: 'status.success.text', borderColor: 'status.success.border' },
  error: { bg: 'status.error.bg', color: 'status.error.text', borderColor: 'status.error.border' },
  info: { bg: 'surface.elevated', color: 'text.secondary', borderColor: 'border.secondary' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setToasts((prev) => [...prev.slice(-(MAX_TOASTS - 1)), { id, message, variant }]);

    const dismissTime = variant === 'error' ? 8000 : 5000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, dismissTime);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Flex
        position="fixed"
        bottom="6"
        right="6"
        direction="column"
        gap="2"
        zIndex="1000"
        maxW="360px"
      >
        {toasts.map((t) => {
          const v = variantStyles[t.variant];
          return (
            <Flex
              key={t.id}
              className="slide-up"
              px="4"
              py="3"
              borderRadius="md"
              boxShadow="lg"
              border="1px solid"
              align="center"
              justify="space-between"
              gap="3"
              fontSize="14px"
              lineHeight="20px"
              bg={v.bg}
              color={v.color}
              borderColor={v.borderColor}
            >
              <Box as="span">{t.message}</Box>
              <Box
                as="button"
                onClick={() => dismiss(t.id)}
                p="2px"
                color="inherit"
                opacity="0.6"
                flexShrink="0"
                cursor="pointer"
                bg="transparent"
                border="none"
                aria-label="Dismiss"
              >
                &#x2715;
              </Box>
            </Flex>
          );
        })}
      </Flex>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
